import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   EventEmitter,
   Input,
   OnDestroy,
   Output,
   ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { CallState } from '@lcs/shared/api';
import { Actions, ofType } from '@ngrx/effects';
import { PromptTypes } from 'generated/graphql-types';
import { Observable, Subject, Subscription, of } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { ExtensionPrivileges } from 'src/app/pages/extension-details/models/extension-privileges';
import { promptItemDialogSubmit } from 'src/app/pages/extension-details/state/extension-detail.actions';
import { AudioPlaylistPlayerService } from '../audio-playlist-player/services/audio-playlist-player.service';
import { ConfirmationDialogService } from '../confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from '../confirmation-dialog/enumerations/dialog-size.enum';
import { QueuePeriodicAnnouncementItemView } from './models/queue-periodic-announcement-item.model';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    selector: 'ndt-queue-periodic-announcement-list',
    styleUrls: ['./queue-periodic-announcement-list.component.scss'],
    templateUrl: './queue-periodic-announcement-list.component.html',
    standalone: false
})
export class QueuePeriodicAnnouncementListComponent implements ControlValueAccessor, OnDestroy {
   @Input() accountId: number;

   @Input() callState: CallState;

   @Input() extensionPrivileges: ExtensionPrivileges;

   @Output() addExistingPromptClick = new EventEmitter<void>();

   @Output() addNewPromptClick = new EventEmitter<PromptTypes>();

   queuePeriodicAnnouncementItems: QueuePeriodicAnnouncementItemView[] = [];

   fileTokens: string[] = [];

   disabled = false;

   onTouched: () => void;

   private destroyed$ = new Subject<void>();

   private onChange: (value: Array<QueuePeriodicAnnouncementItemView> | null) => void;

   showAudioPlayer: boolean;

   currentPlaylistIndex: number;

   isPaused: boolean;

   isLoading: boolean;

   isPlaylistFinished: boolean;

   showError: boolean;

   autoPlayAll: boolean;

   playlistIndexSubscription: Subscription;

   isPausedSubscription: Subscription;

   isLoadingSubscription: Subscription;

   showErrorSubscription: Subscription;

   autoPlayAllSubscription: Subscription;

   promptTypes: typeof PromptTypes = PromptTypes;

   constructor(
      private changeDetectorRef: ChangeDetectorRef,
      control: NgControl,
      private actions$: Actions,
      private confirmationService: ConfirmationDialogService,
      public audioService: AudioPlaylistPlayerService
   ) {
      control.valueAccessor = this;
      this.actions$.pipe(ofType(promptItemDialogSubmit), takeUntil(this.destroyed$)).subscribe((action) => {
         const promptToAdd: QueuePeriodicAnnouncementItemView = {
            isDeleted: false,
            prompt: {
               file: {
                  fileToken: action.promptItem.file?.fileToken ?? '',
               },
               name: action.promptItem.name ?? '',
               promptId: action.promptItem.promptId,
            },
            queuePeriodicAnnouncementId: 0,
            sortOrder: (this.queuePeriodicAnnouncementItems?.length ?? 0) + 1,
         };
         this.queuePeriodicAnnouncementItems.push(promptToAdd);
         this.fileTokens = this.queuePeriodicAnnouncementItems
            .filter((item) => !item.isDeleted)
            .map((item) => item.prompt.file.fileToken);
         this.onChange(this.queuePeriodicAnnouncementItems);
         this.audioService.setPlaylistIndex(-1);
         this.changeDetectorRef.markForCheck();
      });
      this.subscribeToAudioService();
      this.showAudioPlayer = true;
   }

   deleteConfirmationDialog(): Observable<boolean> {
      const message = 'Changes will not take effect until you save the form.';

      const dialogRef = this.confirmationService.confirm({
         data: {
            header: 'Delete this announcement?',
            message,
         },
         size: DialogSize.Medium,
      });

      return dialogRef.afterClosed().pipe(
         takeUntil(this.destroyed$),
         switchMap((confirmed) => {
            if (confirmed) {
               return of(true);
            } else {
               return of(false);
            }
         })
      );
   }

   onDeleteClick(index: number): void {
      this.deleteConfirmationDialog()
         .pipe(takeUntil(this.destroyed$))
         .subscribe((confirmed) => {
            if (confirmed) {
               this.queuePeriodicAnnouncementItems[index].isDeleted = true;
               this.fileTokens = this.queuePeriodicAnnouncementItems
                  .filter((item) => !item.isDeleted)
                  .map((item) => item.prompt.file.fileToken);
               this.audioService.setPlaylistIndex(-1);
               this.changeDetectorRef.markForCheck();
               this.onChange(this.queuePeriodicAnnouncementItems);
            }
         });
   }

   drop(event: CdkDragDrop<QueuePeriodicAnnouncementItemView[]>): void {
      moveItemInArray(this.queuePeriodicAnnouncementItems, event.previousIndex, event.currentIndex);
      for (let i = 0; i < this.queuePeriodicAnnouncementItems.length; i++) {
         this.queuePeriodicAnnouncementItems[i].sortOrder = i + 1;
      }

      this.queuePeriodicAnnouncementItems = [...this.queuePeriodicAnnouncementItems];
      this.fileTokens = this.queuePeriodicAnnouncementItems
         .filter((item) => !item.isDeleted)
         .map((item) => item.prompt.file.fileToken);
      this.audioService.setPlaylistIndex(-1);
      if (this.autoPlayAll) this.audioService.setAutoPlayAll(false);
      this.onChange(this.queuePeriodicAnnouncementItems);
      this.changeDetectorRef.markForCheck();
   }

   writeValue(promptItems: QueuePeriodicAnnouncementItemView[]): void {
      if (promptItems?.length > 0) {
         this.queuePeriodicAnnouncementItems = this.sortPrompts(
            promptItems.map((item) => {
               return { ...item, isDeleted: false };
            })
         );
      } else {
         this.queuePeriodicAnnouncementItems = [];
      }
   }

   registerOnChange(onChange: (value: Array<QueuePeriodicAnnouncementItemView> | null) => void): void {
      this.fileTokens = this.queuePeriodicAnnouncementItems
         .filter((item) => !item.isDeleted)
         .map((item) => item.prompt.file.fileToken);
      this.onChange = onChange;
   }

   registerOnTouched(onTouched: () => void): void {
      this.onTouched = onTouched;
   }

   setDisabledState?(isDisabled: boolean): void {
      this.disabled = isDisabled;
      this.changeDetectorRef.markForCheck();
   }

   onAddExistingPromptClick() {
      this.addExistingPromptClick.emit();
   }

   onPlayAllClick() {
      this.audioService.setAutoPlayAll(true);
      this.changeDetectorRef.markForCheck();
   }

   onStopAllClick() {
      this.audioService.setAutoPlayAll(false);
      this.changeDetectorRef.markForCheck();
   }

   onPlayClick(promptArrayIndex: number, event: MouseEvent) {
      if (!this.showAudioPlayer) {
         this.audioService.setIsLoading(true);
         this.audioService.setPlaylistIndex(promptArrayIndex);
         this.showAudioPlayer = true;
      }
      if (promptArrayIndex === this.currentPlaylistIndex) {
         if (event) {
            return;
         } // avoid infinite loop if user double clicks play/pause
         this.audioService.setIsPaused(!this.isPaused);
      } else {
         this.audioService.setPlaylistIndex(promptArrayIndex);
      }
   }

   subscribeToAudioService() {
      this.playlistIndexSubscription = this.audioService.currentPlaylistIndex
         .pipe(takeUntil(this.destroyed$))
         .subscribe((value) => {
            this.currentPlaylistIndex = value;
            this.changeDetectorRef.markForCheck();
         });
      this.isPausedSubscription = this.audioService.isPaused.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
         this.isPaused = value;
         this.changeDetectorRef.markForCheck();
      });
      this.isLoadingSubscription = this.audioService.isLoading.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
         this.isLoading = value;
         this.changeDetectorRef.markForCheck();
      });
      this.showErrorSubscription = this.audioService.showError.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
         this.showError = value;
         this.changeDetectorRef.markForCheck();
      });
      this.autoPlayAllSubscription = this.audioService.autoPlayAll
         .pipe(takeUntil(this.destroyed$))
         .subscribe((value) => {
            this.autoPlayAll = value;
            this.changeDetectorRef.markForCheck();
         });
   }

   onAddNewPromptClick(promptType: PromptTypes) {
      this.addNewPromptClick.emit(promptType);
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
      this.audioService.setPlaylistIndex(-1);
      this.audioService.setIsPaused(true);
      this.audioService.setIsLoading(false);
      this.audioService.setShowError(false);
      this.audioService.setAutoPlayAll(false);
   }

   private sortPrompts(promptsList: QueuePeriodicAnnouncementItemView[]): QueuePeriodicAnnouncementItemView[] {
      return promptsList.sort((p1, p2) => {
         if (!p1.sortOrder && !p2.sortOrder) {
            return 0;
         }

         if (!p1.sortOrder || p1.sortOrder > p2.sortOrder) {
            return 1;
         }

         if (!p2.sortOrder || p1.sortOrder < p2.sortOrder) {
            return -1;
         }

         return 0;
      });
   }
}
